/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useRef, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import Shimmer from 'react-shimmer-effect';
import Checkbox from '@material-ui/core/Checkbox';

import {
  saveCalendarExercise,
  saveNonDateBasedWorkoutExercise,
  updateProgrammedCalendarItem,
  updateSavedWorkoutItem,
  swapProgrammedItemOnChildCalendar,
  setCombineWithInfo,
} from '../../ducks/calendarActions';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const OrderedList = styled('ol')`
  color: #444444;
  font-family: 'Nunito Sans';
  padding-left: 24px;
`;

const ShimmeringList = styled('ol')`
  color: #444444;
  font-family: 'Nunito Sans';
  margin-bottom: 0px;
  padding-left: 10px;
  margin-top: 5px;
`;

const ListItem = styled('li')`
  margin-top: 5px;
  font-size: 17px;
`;

const ShimmerListItem = styled('li')`
  width: 100%;
  font-size: 17px;
  height: 20px;
  border-radius: 4px;
`;

const QuestionnaireContentContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

const RequireResponseContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

const RequireResponseLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 15px;
  color: #444444;
  margin-bottom: 8px;
  font-weight: 600;
`;

const CheckboxContainer = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-items: center;
`;

const DescriptionText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 15px;
  color: #444444;
  font-weight: 400;
  line-height: 1.4;
`;

const alreadySavedGlobal = new Set();

const QuestionnaireTable = ({
  contentIndex,
  content,
  localAdditionalInfo,
  localDescription,
  swapDropdownSelectedExercise,
  nonSwappedItemFromParentCalendar,
  date,
  dayNumber,
}) => {
  const dispatch = useDispatch();
  const [isAdding, setIsAdding] = useState(false);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const formatDate = () => `${moment(date).format('YYYY-MM-DD')}`;
  const combineWithInfo = useSelector((state) => state.calendar.data.combineWithInfo);
  const programType = useSelector((state) => state.calendar.data.programType);
  const [requireResponseChecked, setRequireResponseChecked] = useState(content.completionRequired);

  const errorAddReset = () => {
    setIsAdding(false);
  };

  const handleAutoSaveCalendarQuestionnaire = (contentIndexParam) => {
    setIsAdding(true);
    const setSubmissionArray = [];
    const submissionObject = {
      /** Conditional key based on programType: "date" OR "dayNumber" */
      [programType === 'free-form-program' ? 'dayNumber' : 'date']:
          programType === 'free-form-program'
            ? dayNumber
            : moment(date).format('YYYY-MM-DD'),
      exerciseId: content.exerciseId,
      type: 'questionnaire',
      sets: setSubmissionArray,
      showSessionBreakItems: false,
    };
    if (localDescription || content.description) {
      submissionObject.description = localDescription || content.description;
    }
    if (localAdditionalInfo) {
      submissionObject.additionalInfo = localAdditionalInfo;
    }
    if (content.groupingLetter) {
      submissionObject.groupingLetter = content.groupingLetter;
    }
    const formattedDate = moment(date).format('YYYY-MM-DD');
    if (programType === 'free-form-program') {
      dispatch(saveNonDateBasedWorkoutExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        dayNumber,
        errorAddReset,
        {},
        content.assignedId,
      ));
    } else {
      dispatch(saveCalendarExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        formattedDate,
        errorAddReset,
        {},
        content.assignedId,
      ));
    }
  };

  /** the CellMeasurer wrapped around each row in the virtualized list
     * can cause remounting of the item a few times as it measures
     * the "just in time" height, this can cause multiple firings of this
     * handleAutoSave funtion (which is a problem because it will create 3 of the same items in the data base).
     * To get around that we save the item by id
     * in the global set that will persist renders and if we have alreay saved
     * the item them we will not fire that function again.
     */

  useEffect(() => {
    if (content.preSave && !alreadySavedGlobal.has(content.assignedId)) {
      alreadySavedGlobal.add(content.assignedId);
      handleAutoSaveCalendarQuestionnaire(contentIndex);
    }
  }, [content.assignedId]);

  /** This useEffect listens for changes is the exercise chosen from the
    * table dropdown menu
    */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      setIsAdding(true);
      const updatedItem = {
        assignedId: content.assignedId,
        date: programType === 'free-form-program' ? '' : formatDate(date),
        exerciseId: swapDropdownSelectedExercise.exerciseId,
        type: 'questionnaire',
      };
      if (programType === 'free-form-program') {
        updatedItem.dayNumber = dayNumber;
      }
      if (programType === 'free-form-program') {
        const reorder = false;
        dispatch(updateSavedWorkoutItem(
          currentUser.accountCode, updatedItem, contentIndex, dayNumber, reorder, {
            onSuccess: () => {
              setIsAdding(false);
            },
            onError: () => {
              setIsAdding(false);
            },
          },
        ));
      }
      if (programType === 'calendar') {
        if (nonSwappedItemFromParentCalendar) {
          const reorder = false;
          dispatch(swapProgrammedItemOnChildCalendar(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {
                setIsAdding(false);
              },
              onError: () => {
                setIsAdding(false);
              },
            },
          ));
        } else {
          const reorder = false;
          dispatch(updateProgrammedCalendarItem(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {
                setIsAdding(false);
              },
              onError: () => {
                setIsAdding(false);
              },
            },
          ));
        }
      }
    }
  }, [swapDropdownSelectedExercise, nonSwappedItemFromParentCalendar]);

  useEffect(() => {
    const isSource = combineWithInfo?.source?.length
      ? combineWithInfo.source.some((item) => item?.assignedId.toString() === content?.assignedId.toString())
      : false;

    const sourceItem = combineWithInfo?.source?.find(
      (item) => item?.assignedId.toString() === content?.assignedId.toString(),
    );

    const isTarget = combineWithInfo?.target?.assignedId === content?.assignedId;

    const targetItem = combineWithInfo?.target;

    if (!content.preSave && combineWithInfo && ((isSource) || (isTarget))) {
      dispatch(setCombineWithInfo(null));
      setIsAdding(true);
      const updatedItem = {
        ...content,
        assignedId: content.assignedId,
        date: programType === 'free-form-program' ? '' : formatDate(date),
        exerciseId: content.exerciseId,
        groupingLetter: isSource
          ? sourceItem?.groupingLetter
          : isTarget
            ? targetItem?.groupingLetter
            : content?.groupingLetter,

        groupingColorCode: isSource
          ? sourceItem?.groupingColorCode
          : isTarget
            ? targetItem?.groupingColorCode
            : content?.groupingColorCode,
        type: 'questionnaire',
      };
      const reorder = true;
      if (programType === 'free-form-program') {
        updatedItem.dayNumber = dayNumber;
      }
      if (programType === 'free-form-program') {
        dispatch(updateSavedWorkoutItem(
          currentUser.accountCode, updatedItem, contentIndex, dayNumber, reorder, {
            onSuccess: () => {
              setIsAdding(false);
            },
            onError: () => {
              setIsAdding(false);
            },
          },
        ));
      }
      if (programType === 'calendar') {
        if (nonSwappedItemFromParentCalendar) {
          dispatch(swapProgrammedItemOnChildCalendar(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {
                setIsAdding(false);
              },
              onError: () => {
                setIsAdding(false);
              },
            },
          ));
        } else {
          dispatch(updateProgrammedCalendarItem(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {
                setIsAdding(false);
              },
              onError: () => {
                setIsAdding(false);
              },
            },
          ));
        }
      }
    }
  }, [combineWithInfo, nonSwappedItemFromParentCalendar]);

  const handleRequireResponseChange = () => {
    const newCheckedState = !requireResponseChecked;
    setRequireResponseChecked(newCheckedState);
    const updatedItem = {
      assignedId: content.assignedId,
      date: programType === 'free-form-program' ? '' : formatDate(date),
      exerciseId: content.exerciseId,
      type: 'questionnaire',
      completionRequired: newCheckedState,
      calendarId: content.calendarId,
      order: content.order,
      description: content.description,
      groupingLetter: content.groupingLetter,
    };
    if (programType === 'free-form-program') {
      updatedItem.dayNumber = dayNumber;
    }

    if (programType === 'free-form-program') {
      dispatch(updateSavedWorkoutItem(
        currentUser.accountCode,
        updatedItem,
        contentIndex,
        dayNumber,
        false,
        {
          onSuccess: () => {
            setIsAdding(false);
          },
          onError: () => {
            setIsAdding(false);
            setRequireResponseChecked(!newCheckedState); // Revert the checkbox state on error
          },
        },
      ));
    } else {
      dispatch(updateProgrammedCalendarItem(
        currentUser.accountCode,
        updatedItem,
        contentIndex,
        formatDate(date),
        false,
        {
          onSuccess: () => {
            setIsAdding(false);
          },
          onError: () => {
            setIsAdding(false);
            setRequireResponseChecked(!newCheckedState); // Revert the checkbox state on error
          },
        },
      ));
    }
  };

  return (
    <Container>
      <QuestionnaireContentContainer>
        {!isAdding ? (
          <OrderedList>
            {content && content.sets ? content?.sets.map((item) => (
              <ListItem>
                {item.questionTitle}
              </ListItem>
            )) : null}
          </OrderedList>
        ) : (
          <ShimmeringList>
            <Shimmer><ShimmerListItem /></Shimmer>
            <Shimmer><ShimmerListItem /></Shimmer>
            <Shimmer><ShimmerListItem /></Shimmer>
          </ShimmeringList>
        )}
        {/* Require Response Checkbox */}
        <RequireResponseContainer>
          <RequireResponseLabel>
            Require Response
          </RequireResponseLabel>
          <CheckboxContainer>
            <Checkbox
              id='require-response-checkbox'
              checked={requireResponseChecked}
              onChange={handleRequireResponseChange}
              color='primary'
              style={{ padding: 0 }}
            />
            <DescriptionText>
              This Questionnaire must be fully completed before showing workout items below it
            </DescriptionText>
          </CheckboxContainer>
        </RequireResponseContainer>
      </QuestionnaireContentContainer>
    </Container>
  );
};

QuestionnaireTable.propTypes = {
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  localAdditionalInfo: PropTypes.string,
  localDescription: PropTypes.string,
  nonSwappedItemFromParentCalendar: PropTypes.bool.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
};

QuestionnaireTable.defaultProps = {
  localAdditionalInfo: '',
  localDescription: '',
  date: null,
  dayNumber: null,
};

export default QuestionnaireTable;
